import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { buildStaticMapUrl, MAPBOX_CONFIG } from '@tourfold/libraries/shared-ui';

/**
 * Thumbnail map shown on each area card.
 *
 * When a Mapbox access token is available we request a real basemap from the Static Images
 * API with the polygon as a `geojson(...)` overlay. The URL builder simplifies the polygon
 * if the encoded URL would exceed Mapbox's ~8 KB limit. If the request fails (404, no
 * token, network error) we fall back to an inline SVG basemap so cards never render
 * blank. Either way only the card's own area is drawn — no surrounding areas.
 *
 * Cards are visually keyed by area.id + lockVersion via the upstream `<img>` so the
 * browser cache stays warm as long as geometry doesn't change.
 */
@Component({
  selector: 'tf-settings-area-mini-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './area-mini-map.component.html',
  styleUrl: './area-mini-map.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaMiniMap {
  private readonly mapboxConfig = inject(MAPBOX_CONFIG, { optional: true });

  readonly color = input<string>('#3B82F6');
  readonly geoJson = input<unknown>(null);
  readonly ariaLabel = input<string>('');

  // Flips to true if the static-image request errors out so we fall back to the inline SVG.
  readonly staticImageFailed = signal(false);

  readonly staticImageUrl = computed(() => {
    const token = this.mapboxConfig?.accessToken;
    if (!token) return null;
    if (this.staticImageFailed()) return null;
    return buildStaticMapUrl({
      accessToken: token,
      width: 320,
      height: 180,
      color: this.color(),
      geoJson: this.geoJson(),
    });
  });

  readonly focusedPath = computed(() => this.toSvgPath(this.geoJson()));

  onStaticImageError(): void {
    this.staticImageFailed.set(true);
  }

  /**
   * Project a GeoJSON polygon / multi-polygon into an SVG path scaled to fit the 160×90 viewport.
   * Returns an empty string if the input isn't a recognisable polygon — the caller renders nothing.
   */
  private toSvgPath(geoJson: unknown): string {
    const rings = this.extractRings(geoJson);
    if (rings.length === 0) return '';

    let minLon = Number.POSITIVE_INFINITY;
    let maxLon = Number.NEGATIVE_INFINITY;
    let minLat = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    for (const ring of rings) {
      for (const [lon, lat] of ring) {
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    }
    const lonSpan = maxLon - minLon || 1;
    const latSpan = maxLat - minLat || 1;
    // Pad 10% so the polygon never touches the edge of the thumbnail.
    const padX = 8;
    const padY = 6;
    const w = 160 - padX * 2;
    const h = 90 - padY * 2;
    // Preserve aspect ratio: fit the longer dimension; center the other.
    const scale = Math.min(w / lonSpan, h / latSpan);
    const offsetX = padX + (w - lonSpan * scale) / 2;
    const offsetY = padY + (h - latSpan * scale) / 2;

    const projectX = (lon: number) => offsetX + (lon - minLon) * scale;
    // Flip Y so North is up.
    const projectY = (lat: number) => offsetY + (maxLat - lat) * scale;

    const segments: string[] = [];
    for (const ring of rings) {
      ring.forEach(([lon, lat], i) => {
        segments.push(
          `${i === 0 ? 'M' : 'L'}${projectX(lon).toFixed(2)},${projectY(lat).toFixed(2)}`
        );
      });
      segments.push('Z');
    }
    return segments.join(' ');
  }

  /**
   * Walk a GeoJSON value (Feature / FeatureCollection / Polygon / MultiPolygon) and
   * return its exterior + holes / sub-polygons as raw [lon, lat] coordinate arrays.
   */
  private extractRings(value: unknown): [number, number][][] {
    if (!value || typeof value !== 'object') return [];
    const node = value as Record<string, unknown>;
    const type = node['type'];
    if (type === 'FeatureCollection') {
      const features = node['features'];
      if (!Array.isArray(features)) return [];
      return features.flatMap((f) => this.extractRings(f));
    }
    if (type === 'Feature') return this.extractRings(node['geometry']);
    if (type === 'Polygon') return this.parsePolygonRings(node['coordinates']);
    if (type === 'MultiPolygon') {
      const coords = node['coordinates'];
      if (!Array.isArray(coords)) return [];
      return coords.flatMap((p) => this.parsePolygonRings(p));
    }
    return [];
  }

  private parsePolygonRings(coords: unknown): [number, number][][] {
    if (!Array.isArray(coords)) return [];
    return coords
      .map((ring): [number, number][] | null => {
        if (!Array.isArray(ring)) return null;
        return ring
          .map((position): [number, number] | null => {
            if (!Array.isArray(position) || position.length < 2) return null;
            const lon = Number(position[0]);
            const lat = Number(position[1]);
            return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null;
          })
          .filter((p): p is [number, number] => p !== null);
      })
      .filter((r): r is [number, number][] => r !== null && r.length >= 2);
  }
}
